/**
 * Group copy v2 (26/10/2026) — the name a list or a campaign has in every country. SMSWiser names
 * carry their country in front ("AU_ACE_FTD_LAST30", "CZ_ACE_FTD_LAST30", "BE_CASINO_MAINFLOW"), so
 * names are compared without their country's prefix. A country's prefix is read from its own names:
 * the land code is not it (Belgium (FR) is "BEFR", its lists start "BE_"). Inner spaces count
 * ("…_ALL TIME" and "…_ALLTIME" are two lists of one country). A near name (`isNearName`) has the
 * same words but one, and that one differs by a single letter and no number ("+12hrOLD" ⇄
 * "+12hrsOLD"); a word more or less is another list ("ACE_UCLICK" is not "UCLICK"), so is another
 * number ("LAST7" is not "LAST3"). Pure.
 * (user: "when pasting it AUTOMATICALLY understands the country representive")
 * For Media Devoted | Created By Ciro Autiero
 */
const PREFIX = /^([A-Za-z]{2,5})_/;
/** A prefix counts as the country's when this share of its names starts with it. */
const PREFIX_SHARE = 0.3;
/** How alike the letters of two names must be to call them near (Dice coefficient). */
export const NEAR_SCORE = 0.85;

/** The leading token of a name ("AU" of "AU_ACE_FTD"); null = none. */
export const prefixOf = (name: string): string | null =>
  PREFIX.exec(name.trim())?.[1]?.toUpperCase() ?? null;

/** The prefix standing out among a country's names, from how many start with each (and how many names in all). */
export function prefixFromCounts(
  counts: ReadonlyMap<string, number>,
  total: number,
): string | null {
  const [best] = [...counts].sort((a, b) => b[1] - a[1]);
  return best && best[1] >= 3 && best[1] >= total * PREFIX_SHARE
    ? best[0]
    : null;
}

/** The prefix most of a country's names start with; null = none stands out. */
export function countryPrefixOf(names: readonly string[]): string | null {
  const counts = new Map<string, number>();
  for (const n of names) {
    const p = prefixOf(n);
    if (p) counts.set(p, (counts.get(p) ?? 0) + 1);
  }
  return prefixFromCounts(counts, names.length);
}

/** The name without its country's prefix, trimmed, case ignored — equal keys are the same item. */
export function nameKey(name: string, prefix: string | null): string {
  const s = name.trim().toLowerCase();
  const head = prefix ? `${prefix.toLowerCase()}_` : null;
  return head && s.startsWith(head) ? s.slice(head.length).trim() : s;
}

const letters = (key: string) => key.replace(/[^a-z]/g, "");
const digits = (key: string) => (key.match(/\d+/g) ?? []).join(",");

function bigrams(s: string): Map<string, number> {
  const out = new Map<string, number>();
  for (let i = 0; i < s.length - 1; i++) {
    const g = s.slice(i, i + 2);
    out.set(g, (out.get(g) ?? 0) + 1);
  }
  return out;
}

/** How alike two strings are, 0–1 (Dice coefficient on letter pairs). */
export function similarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const [x, y] = [bigrams(a), bigrams(b)];
  let shared = 0;
  for (const [g, n] of x) shared += Math.min(n, y.get(g) ?? 0);
  return (2 * shared) / (a.length - 1 + (b.length - 1));
}

/** How near two keys are: 0 when their numbers differ, else how alike their letters are. */
export const nearScore = (a: string, b: string): number =>
  digits(a) === digits(b) ? similarity(letters(a), letters(b)) : 0;

const words = (key: string) => key.split(/[^a-z0-9.]+/).filter(Boolean);

/** One letter added, dropped or changed (Levenshtein distance ≤ 1). */
function oneEdit(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (a.length === b.length) return a.slice(i + 1) === b.slice(i + 1);
  return a.length > b.length
    ? a.slice(i + 1) === b.slice(i)
    : a.slice(i) === b.slice(i + 1);
}

/** Two name keys of one item in two countries, written a little differently ("+12hrsOLD" ⇄ "+12hrOLD"). */
export function isNearName(a: string, b: string): boolean {
  const [x, y] = [words(a), words(b)];
  if (a === b || x.length !== y.length) return false;
  const differ = x
    .map((w, i) => [w, y[i]!] as const)
    .filter(([v, w]) => v !== w);
  if (differ.length !== 1) return false;
  const [v, w] = differ[0]!;
  return digits(v) === digits(w) && oneEdit(v, w);
}
